import { useEffect, useMemo, useState } from "react";
import CustomButton from "../../../components/CustomButton";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../components/CustomTextField";
import SearchableSelect from "../../../components/Select/SearchableSelect";
import CustomSelect from "../../../components/Select/Select";
import { transactionTypeList } from "../../../util/dropList";
import TransactionHistory from "../../Hospital/Inventory/TransactionHistory";

const defaultDateRange = () => {
  const today = new Date();
  return {
    startDate: new Date(today.getFullYear(), today.getMonth(), 1),
    endDate: today,
  };
};

const emptyForm = (hasStock) => ({
  stockName: null,
  ...defaultDateRange(),
  type: "all",
  stockNameErr: "",
  valueEntered: !!hasStock,
});

const StockTransactionHistoryModal = ({
  open = false,
  onClose = () => {},
  selectedStock = null,
}) => {
  const hasStock = !!selectedStock?.stockId;
  const [form, setForm] = useState(() => emptyForm(hasStock));
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const stockList = useMemo(() => {
    if (!mandateDataRaw) return [];
    try {
      const reqData = JSON.parse(mandateDataRaw);
      const stocks = Array.isArray(reqData?.stocks) ? reqData.stocks : [];
      const stockMap = new Map();
      stocks.forEach((li) => {
        const stockId = li?.stockId ?? null;
        if (!stockId || stockMap.has(stockId)) return;
        const name = li?.tradeName ?? li?.productName ?? "";
        stockMap.set(stockId, {
          name,
          value: stockId,
          stockId,
          _searchText: name?.toLowerCase(),
        });
      });
      return Array.from(stockMap.values());
    } catch {
      return [];
    }
  }, [mandateDataRaw]);

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm(!!selectedStock?.stockId));
  }, [open, selectedStock?.stockId]);

  const showingHistory = hasStock || form.valueEntered;
  const historyStockId = hasStock
    ? selectedStock?.stockId
    : form.stockName?.value;

  const onChange = (name, value) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "stockName") {
        next.stockNameErr = value?.value ? "" : "This field is required";
      }
      return next;
    });
  };

  const onGenerate = () => {
    if (!form?.stockName?.value) {
      setForm((prev) => ({
        ...prev,
        stockNameErr: "This field is required",
      }));
      return;
    }
    setForm((prev) => ({ ...prev, valueEntered: true }));
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      header="Stock Transaction History"
      modal={showingHistory}
      rightModal={!showingHistory}
      modalWidth={showingHistory ? 95 : 30}
    >
      <div className="scroll-90vh">
        {showingHistory ? (
          <TransactionHistory
            key={historyStockId}
            selectedStock={{ stockId: historyStockId }}
            initialStartDate={form.startDate}
            initialEndDate={form.endDate}
            initType={form.type ?? "all"}
          />
        ) : (
          <div className="ph20">
            <div className="txt-semi-bold fs14 light-grey">
              Stock Name <span className="red-color">*</span>
            </div>
            <SearchableSelect
              list={stockList}
              value={form?.stockName ?? null}
              handleChange={(e, value) => {
                onChange("stockName", value);
              }}
              maxFilteredOptions={100}
              error={!!form?.stockNameErr}
              helperText={form?.stockNameErr}
            />
            <div className="flex-row-between mt10">
              <div className="w48Per">
                <div className="txt-semi-bold fs14 light-grey">Start Date</div>
                <CustomTextField
                  value={form?.startDate}
                  fullWidth
                  mobileDate
                  handleChange={(e) => onChange("startDate", e?.target?.value)}
                />
              </div>
              <div className="w48Per">
                <div className="txt-semi-bold fs14 light-grey">End Date</div>
                <CustomTextField
                  value={form?.endDate}
                  fullWidth
                  mobileDate
                  handleChange={(e) => onChange("endDate", e?.target?.value)}
                />
              </div>
            </div>
            <div className="txt-semi-bold fs14 light-grey mt10">Type</div>
            <CustomSelect
              list={transactionTypeList}
              fullWidth
              name="type"
              handleChange={(e) => {
                const { name, value } = e?.target ?? {};
                onChange(name?.trim()?.replace(/,$/, ""), value);
              }}
              value={form?.type ?? "all"}
              label=""
              select
            />
            <div className="flex1-end mt20">
              <div>
                <CustomButton text="Generate" onClick={onGenerate} smallBtn />
              </div>
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default StockTransactionHistoryModal;
